import {AxiosInstance} from 'axios';

/** A role IN a company. The platform role is `AuthUser.role`, never this (SB-24, ADR-0006). */
export type CompanyRole = 'admin' | 'member';

export interface CompanyMember {
    user_id: string;
    email: string | null;
    name: string | null;
    role: CompanyRole;
    /** `invitation`, later `directory` (SB-324). */
    source: string;
    created_at: string | null;
}

export interface CurrentCompany {
    id: string;
    name: string;
    /** The person's own role here; null for the platform role when it is no member. */
    role: CompanyRole | null;
    /** A company admin, or the platform role (SB-318). */
    can_manage: boolean;
    members: CompanyMember[];
}

/** An open invitation, as its company's admins see it (SB-320). */
export interface CompanyInvitation {
    id: string;
    email: string;
    role: CompanyRole;
    created_at: string | null;
    expires_at: string | null;
    expired: boolean;
    /** When the last send left; null when it did not, and then `send_error` says why. */
    sent_at: string | null;
    send_error: string | null;
}

export interface InvitationSent extends CompanyInvitation {
    sent: boolean;
    /** The address had an open invitation already, which went again with a new link. */
    renewed?: boolean;
}

export type InvitationStatus = 'open' | 'expired' | 'withdrawn' | 'accepted';

/** What an invitation link invites to, for its page (`/invitasjon/:token`). */
export interface InvitationView {
    company_name: string;
    /** The address the invitation is for: the one to sign in with. */
    email: string;
    role: CompanyRole;
    status: InvitationStatus;
    expires_at: string;
    signed_in_as: string | null;
    /** Signed in with the invited address, so «Godta» works. */
    matches: boolean;
}

/** The company the session works in (`/api/companies/current`, SB-318), and invitations (SB-320). */
export function createCompaniesApi(client: AxiosInstance) {
    return {
        async getCurrentCompany(): Promise<CurrentCompany> {
            const response = await client.get('/api/companies/current');
            return response.data;
        },
        async renameCompany(name: string): Promise<{id: string; name: string}> {
            const response = await client.patch('/api/companies/current', {name});
            return response.data;
        },
        async setMemberRole(userId: string, role: CompanyRole): Promise<{user_id: string; role: CompanyRole}> {
            const response = await client.patch(`/api/companies/current/members/${encodeURIComponent(userId)}`, {role});
            return response.data;
        },
        /** Remove a member, or leave the company when it is the person's own id. */
        async removeMember(userId: string): Promise<void> {
            await client.delete(`/api/companies/current/members/${encodeURIComponent(userId)}`);
        },
        async listInvitations(): Promise<CompanyInvitation[]> {
            const response = await client.get('/api/companies/current/invitations');
            return response.data;
        },
        /** Invite an address; one with an open invitation gets it again, with a new link. */
        async invite(email: string, role: CompanyRole): Promise<InvitationSent> {
            const response = await client.post('/api/companies/current/invitations', {email, role});
            return response.data;
        },
        async resendInvitation(id: string): Promise<InvitationSent> {
            const response = await client.post(`/api/companies/current/invitations/${encodeURIComponent(id)}/resend`);
            return response.data;
        },
        async withdrawInvitation(id: string): Promise<void> {
            await client.delete(`/api/companies/current/invitations/${encodeURIComponent(id)}`);
        },
        /** The token goes in the body, so the API's access log never holds it. Changes nothing. */
        async viewInvitation(token: string): Promise<InvitationView> {
            const response = await client.post('/api/invitations/view', {token});
            return response.data;
        },
        async acceptInvitation(token: string): Promise<{company: {id: string; role: CompanyRole | null}}> {
            const response = await client.post('/api/invitations/accept', {token});
            return response.data;
        },
    };
}
